import { Button, DialogActions, DialogContent, Divider, Typography } from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';
import { Stack } from '@mui/system';
import PropTypes from 'prop-types';
import React from 'react';
import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import { robotSlice, selectAreDrivebaseFirmwareActionsDisabled } from '../../../redux/RobotSlice';
import { ApplicationActions, RobotActions } from '../../../utils/constants';

const CriticalFirmwareUpdateDialog = ({ onClose, robotTypeName }) => {
  const dispatch = useDispatch();
  const { connectionType, firmware } = useSelector((state) => state.robot);
  const firmwareActionsDisabled = useSelector(selectAreDrivebaseFirmwareActionsDisabled);
  const isUsb = connectionType === RobotActions.STATE.CONNECTION_TYPE.SERIAL;
  const shouldFinalizeUpdates = firmware.status === RobotActions.FIRMWARE_STATUS.READY_TO_INSTALL;
  const continueDisabled = firmwareActionsDisabled;

  const handleDisconnect = () => {
    onClose();
    dispatch(robotSlice.actions.onUpdateRobotState(RobotActions.STATE.USER_CANCELLED));
  };

  const handleContinue = () => {
    if (continueDisabled) return;

    const { dialog, options } = ApplicationActions.Dialogs.FIRMWARE_UPDATING;
    const firmwareState = shouldFinalizeUpdates ? RobotActions.FIRMWARE_UPDATE.FINALIZE_UPDATES : RobotActions.FIRMWARE_UPDATE.START_DOWNLOAD;

    onClose();
    dispatch(
      applicationSlice.actions.onOpenDialog({
        dialog,
        options: { ...options, robotTypeName },
      })
    );
    dispatch(robotSlice.actions.onFirmwareStateChange({ firmwareState }));
  };

  return (
    <>
      <DialogTitle variant="sectionTitle" align="center">
        <Trans i18nKey="criticalFirmwareDialog.title">Critical Firmware Update</Trans>
      </DialogTitle>
      <Divider />
      <DialogContent style={{ padding: '3rem 2.75rem' }}>
        <Stack direction="column" spacing={2} useFlexGap flexWrap="wrap">
          <Typography variant="body1">
            <Trans i18nKey="criticalFirmwareDialog.required" values={{ robotTypeName }}>
              Your {{ robotTypeName }} needs a critical firmware update to be used. You will need to wait until the firmware transfer and installation
              is completed before you can use it.
            </Trans>
          </Typography>

          {isUsb ? (
            <Typography variant="body1">
              <Trans i18nKey="criticalFirmwareDialog.usb" values={{ robotTypeName }}>
                During the process, do not power off or unplug your {{ robotTypeName }} and stay connected with your USB cable.
              </Trans>
            </Typography>
          ) : (
            <Typography variant="body1">
              <Trans i18nKey="criticalFirmwareDialog.bluetooth" values={{ robotTypeName }}>
                To start the critical firmware update, you will need to connect your {{ robotTypeName }} to your computer with a USB cable. Disconnect
                from Bluetooth below and reconnect with a USB cable to proceed.
              </Trans>
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions style={{ justifyContent: 'center', margin: '2px 0' }}>
        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
          <Button variant="text" autoFocus onClick={handleDisconnect}>
            <Trans i18nKey="button.disconnect">Disconnect</Trans>
          </Button>
          {isUsb && (
            <Button variant="contained" onClick={handleContinue} disabled={continueDisabled}>
              <Trans i18nKey="button.continue">Continue</Trans>
            </Button>
          )}
        </Stack>
      </DialogActions>
    </>
  );
};

CriticalFirmwareUpdateDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  robotTypeName: PropTypes.string,
};

export default CriticalFirmwareUpdateDialog;
